import { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { RaioXModal } from "@/components/radiox/RaioXModal";
import { formatPhone } from "@/lib/utils";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import SupplierDetails from "@/pages/SupplierDetails";
import { format } from "date-fns";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Plus, Pencil, Trash2, RefreshCcw, Search, FileText, Upload, X, User } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { TablePagination } from "@/components/ui/table-pagination";
import { useTenant } from "@/contexts/TenantContext";
import { getChatwootSDK } from "@/integrations/chatwoot/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useChatwootToken } from "@/hooks/use-chatwoot-token";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { SupplierFilters, SupplierFilterState } from "@/components/suppliers/SupplierFilters";
import { SupplierAvatar } from "@/components/suppliers/SupplierAvatar";
import { logCrud } from "@/services/activities";

type Supplier = {
  id: string;
  nome: string;
  cnpj_cpf: string | null;
  contato: string | null;
  telefone: string | null;
  whatsapp: string | null;
  email: string | null;
  endereco_completo: string | null;
  chatwoot_contact_id: number | null;
  foto_url: string | null;
  chatwoot_avatar_url: string | null;
};

const extractDigits = (value: string) => value.replace(/\D/g, "");

const normalizePhoneDigits = (value: string) => {
  if (!value) return "";
  let digits = extractDigits(value);
  if (!digits) return "";
  if (digits.startsWith("55") && digits.length > 11) {
    digits = digits.slice(2);
  }
  return digits.slice(0, 11);
};

const formatPhoneBR = (value: string | null | undefined) => {
  const digits = normalizePhoneDigits(value || "");
  if (!digits) return "";

  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{0,2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .slice(0, 14);
  }

  return digits
    .replace(/^(\d{0,2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .slice(0, 15);
};

const formatCpfCnpj = (value: string | null | undefined) => {
  const digits = extractDigits((value || "").slice(0, 14));
  if (!digits) return "";

  if (digits.length <= 11) {
    return digits
      .replace(/^(\d{0,3})(\d)/, "$1.$2")
      .replace(/^(\d{3}\.\d{0,3})(\d)/, "$1.$2")
      .replace(/^(\d{3}\.\d{3}\.\d{0,3})(\d{0,2})$/, "$1-$2")
      .slice(0, 14);
  }

  return digits
    .padEnd(14, "")
    .replace(/^(\d{0,2})(\d)/, "$1.$2")
    .replace(/^(\d{2}\.\d{0,3})(\d)/, "$1.$2")
    .replace(/^(\d{2}\.\d{3}\.\d{0,3})(\d)/, "$1/$2")
    .replace(/^(\d{2}\.\d{3}\.\d{3}\/\d{0,4})(\d{0,2})$/, "$1-$2")
    .slice(0, 18);
};

const sanitizeDigits = (value: FormDataEntryValue | null) => {
  if (!value) return null;
  const digits = extractDigits(String(value));
  return digits ? digits : null;
};

const sanitizePhone = (value: FormDataEntryValue | null) => {
  const digits = normalizePhoneDigits(String(value ?? ""));
  return digits ? digits : null;
};

export default function Suppliers() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [syncingSupplierId, setSyncingSupplierId] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [shouldRemovePhoto, setShouldRemovePhoto] = useState(false);
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const { hasToken, markTokenInvalid } = useChatwootToken();
  const [supplierProfile, setSupplierProfile] = useState<{ id: string; name: string } | null>(null);
  const [filters, setFilters] = useState<SupplierFilterState>({
    searchTerm: "",
    hasChatwoot: null,
    hasTelefone: null,
    hasEmail: null,
    hasContato: null,
    hasWhatsapp: null,
  });

  const { data: suppliers, isLoading } = useQuery({
    queryKey: ["suppliers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("suppliers")
        .select("*")
        .order("nome", { ascending: true });
      
      if (error) throw error;
      return data as Supplier[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      if (!tenantId) throw new Error("Tenant ID não encontrado");

      const insertPayload = {
        nome: formData.get("nome") as string,
        cnpj_cpf: sanitizeDigits(formData.get("cnpj_cpf")),
        contato: (formData.get("contato") as string) || null,
        telefone: sanitizePhone(formData.get("telefone")),
        whatsapp: sanitizePhone(formData.get("whatsapp")),
        email: (formData.get("email") as string) || null,
        endereco_completo: (formData.get("endereco_completo") as string) || null,
        tenant_id: tenantId,
      };

      const { data, error } = await supabase
        .from("suppliers")
        .insert(insertPayload)
        .select("id")
        .single();
      if (error) throw error;

      const supplierId = data?.id as string | undefined;
      
      if (photoFile && supplierId) {
        const photoUrl = await uploadPhoto(photoFile, supplierId);
        const { error: updateError } = await supabase
          .from("suppliers")
          .update({ foto_url: photoUrl })
          .eq("id", supplierId);
        
        if (updateError) throw updateError;
      }
      
      if (supplierId && hasToken) {
        try {
          const sdk = getChatwootSDK();
          await sdk.syncContact("supplier", supplierId);
        } catch (syncError: any) {
          console.error("Erro ao sincronizar fornecedor com Chatwoot", syncError);
          handleChatwootFailure(
            syncError,
            "Fornecedor salvo, mas não foi possível sincronizar com o Chatwoot.",
            "warning",
            "Fornecedor salvo, mas o token do Chatwoot é inválido. Atualize o token em Configurações > Integrações > Chatwoot."
          );
        }
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      toast.success("Fornecedor criado com sucesso");
      const name = variables.get("nome") as string;
      void logCrud({ entitySlug: "entity:suppliers", operationSlug: "create", entityName: name, screenSlug: "fornecedores" });
      setOpen(false);
      setPhotoFile(null);
      setPhotoPreview(null);
      setEditingSupplier(null);
      setShouldRemovePhoto(false);
    },
    onError: () => toast.error("Erro ao criar fornecedor"),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, formData }: { id: string; formData: FormData }) => {
      const updatePayload: {
        nome: string;
        cnpj_cpf: string | null;
        contato: string | null;
        telefone: string | null;
        whatsapp: string | null;
        email: string | null;
        endereco_completo: string | null;
        foto_url?: string | null;
      } = {
        nome: formData.get("nome") as string,
        cnpj_cpf: sanitizeDigits(formData.get("cnpj_cpf")),
        contato: (formData.get("contato") as string) || null,
        telefone: sanitizePhone(formData.get("telefone")),
        whatsapp: sanitizePhone(formData.get("whatsapp")),
        email: (formData.get("email") as string) || null,
        endereco_completo: (formData.get("endereco_completo") as string) || null,
      };

      // Se o usuário clicou para remover a foto
      if (shouldRemovePhoto) {
        if (editingSupplier?.foto_url) {
          await deletePhoto(editingSupplier.foto_url);
        }
        updatePayload.foto_url = null;
      } else if (photoFile) {
        // Se está adicionando uma nova foto
        if (editingSupplier?.foto_url) {
          await deletePhoto(editingSupplier.foto_url);
        }
        const photoUrl = await uploadPhoto(photoFile, id);
        updatePayload.foto_url = photoUrl;
      }

      const { error } = await supabase
        .from("suppliers")
        .update(updatePayload)
        .eq("id", id);
      if (error) throw error;

      if (hasToken) {
        try {
          const sdk = getChatwootSDK();
          await sdk.syncContact("supplier", id);
        } catch (syncError: any) {
          console.error("Erro ao sincronizar fornecedor com Chatwoot", syncError);
          handleChatwootFailure(
            syncError,
            "Fornecedor atualizado, mas não foi possível sincronizar com o Chatwoot.",
            "warning",
            "Fornecedor atualizado, mas o token do Chatwoot é inválido. Atualize o token em Configurações > Integrações > Chatwoot."
          );
        }
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      toast.success("Fornecedor atualizado com sucesso");
      void logCrud({ entitySlug: "entity:suppliers", operationSlug: "update", entityId: (variables as any).id, screenSlug: "fornecedores", before: (editingSupplier as any), after: (variables as any) });
      setOpen(false);
      setEditingSupplier(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      setShouldRemovePhoto(false);
    },
    onError: () => toast.error("Erro ao atualizar fornecedor"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("suppliers").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      toast.success("Fornecedor excluído com sucesso");
      if (deleteId) {
        void logCrud({ entitySlug: "entity:suppliers", operationSlug: "delete", entityId: deleteId, screenSlug: "fornecedores" });
      }
      setDeleteId(null);
    },
    onError: (error: any) => {
      const isFK = error?.code === "23503" || error?.message?.includes("foreign key constraint") || error?.status === 409;
      if (isFK) {
        toast.error(
          "Não é possível excluir este fornecedor porque existem registros relacionados (ex.: transações). Remova ou ajuste os vínculos antes de tentar novamente."
        );
      } else {
        toast.error("Erro ao excluir fornecedor");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    if (editingSupplier) {
      updateMutation.mutate({ id: editingSupplier.id, formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const uploadPhoto = async (file: File, supplierId: string) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${supplierId}-${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('supplier-photos')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('supplier-photos')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const deletePhoto = async (url: string) => {
    if (!url) return;
    try {
      const path = url.split('/supplier-photos/')[1];
      if (path) {
        await supabase.storage.from('supplier-photos').remove([path]);
      }
    } catch (error) {
      console.error("Erro ao deletar foto:", error);
    }
  };

  const handleOpenDialog = (supplier?: Supplier) => {
    setEditingSupplier(supplier || null);
    setPhotoFile(null);
    setShouldRemovePhoto(false);
    // Mostrar preview apenas se houver foto real (upload ou Chatwoot), não Gravatar
    if (supplier) {
      const realPhoto = supplier.foto_url || supplier.chatwoot_avatar_url || null;
      setPhotoPreview(realPhoto);
    } else {
      setPhotoPreview(null);
    }
    setOpen(true);
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      setShouldRemovePhoto(false); // Se está adicionando nova foto, não remover
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setShouldRemovePhoto(true); // Marcar que a foto deve ser removida
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const getPrimaryContact = (supplier: Supplier) => {
    const whatsapp = formatPhoneBR(supplier.whatsapp || "");
    if (whatsapp) return whatsapp;

    const telefone = formatPhoneBR(supplier.telefone || "");
    if (telefone) return telefone;

    return supplier.email || "-";
  };

  const getWhatsappDisplay = (supplier: Supplier) => {
    const whatsapp = formatPhoneBR(supplier.whatsapp || "");
    return whatsapp || "—";
  };

  const handleChatwootFailure = (
    error: unknown,
    fallbackMessage: string,
    variant: "error" | "warning" = "error",
    tokenInvalidMessage?: string
  ) => {
    const message = typeof (error as any)?.message === "string" ? (error as any).message : "";
    if (message.includes("401")) {
      markTokenInvalid();
      toast.error(
        tokenInvalidMessage ||
          `${fallbackMessage} Token do Chatwoot inválido. Atualize o token em Configurações > Integrações > Chatwoot.`
      );
    } else {
      if (variant === "warning") {
        toast.warning(message || fallbackMessage);
      } else {
        toast.error(message || fallbackMessage);
      }
    }
  };

  // Abrir modal via query string e limpar parâmetro
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("newSupplier") === "1") {
      handleOpenDialog();
      params.delete("newSupplier");
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [location.search]);

  const matchesFilters = (supplier: Supplier) => {
    // Filtro por busca
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const matchesSearch =
        supplier.nome?.toLowerCase().includes(searchLower) ||
        supplier.cnpj_cpf?.toLowerCase().includes(searchLower) ||
        supplier.email?.toLowerCase().includes(searchLower) ||
        supplier.telefone?.toLowerCase().includes(searchLower) ||
        supplier.whatsapp?.toLowerCase().includes(searchLower) ||
        supplier.contato?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Filtro por Telefone
    if (filters.hasTelefone !== null) {
      const hasTelefone = Boolean(supplier.telefone && supplier.telefone.trim() !== "");
      if (filters.hasTelefone !== hasTelefone) return false;
    }

    // Filtro por Email
    if (filters.hasEmail !== null) {
      const hasEmail = Boolean(supplier.email && supplier.email.trim() !== "");
      if (filters.hasEmail !== hasEmail) return false;
    }

    // Filtro por Contato
    if (filters.hasContato !== null) {
      const hasContato = Boolean(supplier.contato && supplier.contato.trim() !== "");
      if (filters.hasContato !== hasContato) return false;
    }

    // Filtro por WhatsApp
    if (filters.hasWhatsapp !== null) {
      const hasWhatsapp = Boolean(supplier.whatsapp && supplier.whatsapp.trim() !== "");
      if (filters.hasWhatsapp !== hasWhatsapp) return false;
    }

    // Filtro por Chatwoot
    if (filters.hasChatwoot !== null) {
      const hasChatwoot = Boolean(supplier.chatwoot_contact_id);
      if (filters.hasChatwoot !== hasChatwoot) return false;
    }

    return true;
  };

  const filteredSuppliers = useMemo(() => {
    if (!suppliers) return [] as Supplier[];
    return suppliers.filter((supplier) => matchesFilters(supplier));
  }, [suppliers, filters]);

  const paginatedSuppliers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredSuppliers.slice(start, start + itemsPerPage);
  }, [filteredSuppliers, currentPage, itemsPerPage]);

  // Calcular contagens para os filtros
  const facetCounts = useMemo(() => {
    const counts = {
      hasChatwoot: {
        vinculado: 0,
        naoVinculado: 0,
      },
      hasTelefone: {
        com: 0,
        sem: 0,
      },
      hasEmail: {
        com: 0,
        sem: 0,
      },
      hasContato: {
        com: 0,
        sem: 0,
      },
      hasWhatsapp: {
        com: 0,
        sem: 0,
      },
    };

    if (!suppliers) return counts;

    suppliers.forEach((supplier) => {
      const matchesOtherFilters = (exceptFilter?: "hasChatwoot" | "hasTelefone" | "hasEmail" | "hasContato" | "hasWhatsapp") => {
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          const matchesSearch =
            supplier.nome?.toLowerCase().includes(searchLower) ||
            supplier.cnpj_cpf?.toLowerCase().includes(searchLower) ||
            supplier.email?.toLowerCase().includes(searchLower) ||
            supplier.telefone?.toLowerCase().includes(searchLower) ||
            supplier.whatsapp?.toLowerCase().includes(searchLower) ||
            supplier.contato?.toLowerCase().includes(searchLower);
          if (!matchesSearch) return false;
        }

        if (exceptFilter !== "hasChatwoot" && filters.hasChatwoot !== null) {
          const hasChatwoot = Boolean(supplier.chatwoot_contact_id);
          if (filters.hasChatwoot !== hasChatwoot) return false;
        }

        if (exceptFilter !== "hasTelefone" && filters.hasTelefone !== null) {
          const hasTelefone = Boolean(supplier.telefone && supplier.telefone.trim() !== "");
          if (filters.hasTelefone !== hasTelefone) return false;
        }

        if (exceptFilter !== "hasEmail" && filters.hasEmail !== null) {
          const hasEmail = Boolean(supplier.email && supplier.email.trim() !== "");
          if (filters.hasEmail !== hasEmail) return false;
        }

        if (exceptFilter !== "hasContato" && filters.hasContato !== null) {
          const hasContato = Boolean(supplier.contato && supplier.contato.trim() !== "");
          if (filters.hasContato !== hasContato) return false;
        }

        if (exceptFilter !== "hasWhatsapp" && filters.hasWhatsapp !== null) {
          const hasWhatsapp = Boolean(supplier.whatsapp && supplier.whatsapp.trim() !== "");
          if (filters.hasWhatsapp !== hasWhatsapp) return false;
        }

        return true;
      };

      if (matchesOtherFilters("hasChatwoot")) {
        if (supplier.chatwoot_contact_id) {
          counts.hasChatwoot.vinculado++;
        } else {
          counts.hasChatwoot.naoVinculado++;
        }
      }

      if (matchesOtherFilters("hasTelefone")) {
        const hasTelefone = Boolean(supplier.telefone && supplier.telefone.trim() !== "");
        if (hasTelefone) {
          counts.hasTelefone.com++;
        } else {
          counts.hasTelefone.sem++;
        }
      }

      if (matchesOtherFilters("hasEmail")) {
        const hasEmail = Boolean(supplier.email && supplier.email.trim() !== "");
        if (hasEmail) {
          counts.hasEmail.com++;
        } else {
          counts.hasEmail.sem++;
        }
      }

      if (matchesOtherFilters("hasContato")) {
        const hasContato = Boolean(supplier.contato && supplier.contato.trim() !== "");
        if (hasContato) {
          counts.hasContato.com++;
        } else {
          counts.hasContato.sem++;
        }
      }

      if (matchesOtherFilters("hasWhatsapp")) {
        const hasWhatsapp = Boolean(supplier.whatsapp && supplier.whatsapp.trim() !== "");
        if (hasWhatsapp) {
          counts.hasWhatsapp.com++;
        } else {
          counts.hasWhatsapp.sem++;
        }
      }
    });

    return counts;
  }, [suppliers, filters]);

  const handleFiltersChange = (newFilters: SupplierFilterState) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const activeFilterChips = useMemo<{ id: string; label: React.ReactNode; onClear: () => void }[]>(() => {
    const chips: { id: string; label: React.ReactNode; onClear: () => void }[] = [];
    
    if (filters.searchTerm) {
      const term = filters.searchTerm;
      chips.push({
        id: `search:${term}`,
        label: `Busca: "${term}"`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, searchTerm: "" });
        },
      });
    }
    
    if (filters.hasTelefone !== null) {
      chips.push({
        id: `telefone:${filters.hasTelefone}`,
        label: `Telefone: ${filters.hasTelefone ? "Com" : "Sem"}`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, hasTelefone: null });
        },
      });
    }

    if (filters.hasEmail !== null) {
      chips.push({
        id: `email:${filters.hasEmail}`,
        label: `E-mail: ${filters.hasEmail ? "Com" : "Sem"}`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, hasEmail: null });
        },
      });
    }

    if (filters.hasContato !== null) {
      chips.push({
        id: `contato:${filters.hasContato}`,
        label: `Contato: ${filters.hasContato ? "Com" : "Sem"}`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, hasContato: null });
        },
      });
    }

    if (filters.hasWhatsapp !== null) {
      chips.push({
        id: `whatsapp:${filters.hasWhatsapp}`,
        label: `WhatsApp: ${filters.hasWhatsapp ? "Com" : "Sem"}`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, hasWhatsapp: null });
        },
      });
    }
    
    if (filters.hasChatwoot !== null) {
      chips.push({
        id: `chatwoot:${filters.hasChatwoot}`,
        label: `Chatwoot: ${filters.hasChatwoot ? "Vinculado" : "Não vinculado"}`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, hasChatwoot: null });
        },
      });
    }
    
    return chips;
  }, [filters]);

  const renderChatwootStatus = (supplier: Supplier) => {
    if (!hasToken) return null;

    if (supplier.chatwoot_contact_id) {
      return (
        <span className="inline-flex items-center gap-1 text-xs rounded bg-emerald-100 text-emerald-700 px-2 py-1">
          Vinculado
        </span>
      );
    }

    const hasSyncData = Boolean(supplier.telefone || supplier.whatsapp || supplier.email);

    if (!hasSyncData) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs rounded bg-muted text-muted-foreground px-2 py-1 cursor-not-allowed"
                onClick={(e) => {
                  e.stopPropagation();
                  toast.message("Para sincronizar este contato, adicione telefone e/ou email.");
                }}
              >
                <RefreshCcw className="h-3 w-3" />
                Pendente
              </button>
            </TooltipTrigger>
            <TooltipContent>
              Para sincronizar este contato, adicione telefone e/ou email.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <button
        type="button"
        className="inline-flex items-center gap-1 text-xs rounded bg-amber-100 text-amber-700 px-2 py-1 hover:bg-amber-200 transition disabled:opacity-60 disabled:cursor-not-allowed"
        disabled={syncingSupplierId === supplier.id}
        onClick={async (e) => {
          e.stopPropagation();
          if (syncingSupplierId) return;
          try {
            setSyncingSupplierId(supplier.id);
            const sdk = getChatwootSDK();
            await sdk.syncContact("supplier", supplier.id);
            toast.success("Sincronização concluída.");
            queryClient.invalidateQueries({ queryKey: ["suppliers"] });
          } catch (error: any) {
            handleChatwootFailure(error, "Falha ao sincronizar com o Chatwoot.");
          } finally {
            setSyncingSupplierId(null);
          }
        }}
      >
        <RefreshCcw className={`h-3 w-3 ${syncingSupplierId === supplier.id ? "animate-spin" : ""}`} />
        Pendente
      </button>
    );
  };

  return (
    <div className="space-y-6" data-cy="suppliers-page">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold" data-cy="suppliers-title">Fornecedores</h1>
          <p className="text-muted-foreground">Gerencie seus fornecedores</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="w-full md:w-auto" onClick={() => handleOpenDialog()} data-cy="new-supplier-btn">
              <Plus className="mr-2 h-4 w-4" />
              Novo Fornecedor
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl p-0 overflow-hidden" data-cy="supplier-dialog">
            <div className="flex max-h-[90vh] flex-col">
              <DialogHeader className="sticky top-0 z-10 bg-background px-6 py-4 border-b relative">
                <DialogTitle data-cy="supplier-dialog-title">
                  {editingSupplier ? "Editar Fornecedor" : "Novo Fornecedor"}
                </DialogTitle>
                <DialogDescription>
                  Preencha os dados do fornecedor
                </DialogDescription>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-4 h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <form id="supplier-form" onSubmit={handleSubmit} className="space-y-4" data-cy="supplier-form">
              <div className="flex gap-6 items-start">
                {/* Foto do Fornecedor */}
                <div className="flex-shrink-0">
                  <Label className="mb-2 block">Foto do Fornecedor</Label>
                  <Input
                    id="foto"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                  {photoPreview ? (
                    <div className="relative">
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="w-32 h-32 rounded-full object-cover border-2 border-border cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => document.getElementById('foto')?.click()}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePhoto();
                        }}
                        className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90 z-10"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div 
                      className="w-32 h-32 rounded-full bg-muted border-2 border-dashed border-muted-foreground/25 flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors"
                      onClick={() => document.getElementById('foto')?.click()}
                    >
                      <User className="h-16 w-16 text-muted-foreground" />
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Clique para adicionar
                  </p>
                </div>

                {/* Campos ao lado da foto */}
                <div className="flex-1 space-y-4">
                  {/* Primeira linha: Nome/Razão Social e E-mail */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nome">Nome/Razão Social *</Label>
                      <Input
                        id="nome"
                        name="nome"
                        defaultValue={editingSupplier?.nome}
                        required
                        data-cy="supplier-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        defaultValue={editingSupplier?.email || ""}
                        data-cy="supplier-email-input"
                      />
                    </div>
                  </div>

                  {/* Segunda linha: CNPJ/CPF, Contato, Telefone e WhatsApp */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="cnpj_cpf">CNPJ/CPF</Label>
                      <Input
                        id="cnpj_cpf"
                        name="cnpj_cpf"
                        defaultValue={formatCpfCnpj(editingSupplier?.cnpj_cpf || "")}
                        inputMode="numeric"
                        maxLength={18}
                        onChange={(event) => {
                          event.currentTarget.value = formatCpfCnpj(event.currentTarget.value);
                        }}
                        data-cy="supplier-cnpj-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contato">Contato</Label>
                      <Input
                        id="contato"
                        name="contato"
                        defaultValue={editingSupplier?.contato || ""}
                        data-cy="supplier-contato-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="telefone">Telefone</Label>
                      <Input
                        id="telefone"
                        name="telefone"
                        type="tel"
                        defaultValue={formatPhoneBR(editingSupplier?.telefone || "")}
                        maxLength={15}
                        inputMode="tel"
                        onChange={(event) => {
                          event.currentTarget.value = formatPhoneBR(event.currentTarget.value);
                        }}
                        data-cy="supplier-telefone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="whatsapp">WhatsApp</Label>
                      <Input
                        id="whatsapp"
                        name="whatsapp"
                        type="tel"
                        defaultValue={formatPhoneBR(editingSupplier?.whatsapp || "")}
                        maxLength={15}
                        inputMode="tel"
                        onChange={(event) => {
                          event.currentTarget.value = formatPhoneBR(event.currentTarget.value);
                        }}
                        data-cy="supplier-whatsapp-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="endereco_completo">Endereço Completo</Label>
                <Textarea
                  id="endereco_completo"
                  name="endereco_completo"
                  defaultValue={editingSupplier?.endereco_completo || ""}
                  rows={2}
                  data-cy="supplier-endereco-input"
                />
              </div>

              </form>
              </div>
              <div className="bg-background px-6 py-4 border-t flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  form="supplier-form"
                  data-cy="supplier-submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {editingSupplier
                    ? isSubmitting
                      ? "Salvando..."
                      : "Salvar Fornecedor"
                    : isSubmitting
                      ? "Criando..."
                      : "Criar Fornecedor"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle data-cy="suppliers-list-title">Lista de Fornecedores</CardTitle>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="relative w-full sm:w-[200px]">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFiltersChange({ ...filters, searchTerm: e.target.value })}
                    className="pl-8"
                    data-cy="suppliers-search-input"
                  />
                </div>
                <SupplierFilters
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  hasToken={hasToken}
                  counts={facetCounts}
                  data-cy="suppliers-filters"
                />
              </div>
            </div>
            {activeFilterChips.length > 0 && (
              <div className="text-[13px] md:text-sm text-muted-foreground">
                <span className="mr-2 font-bold">Filtros ativos:</span>
                <div className="inline-flex flex-wrap gap-0.5">
                  {activeFilterChips.map((chip) => (
                    <Badge key={chip.id} variant="secondary" className="h-5 px-1.5 py-0 inline-flex items-center gap-1 border border-gray-300">
                      <span>{chip.label}</span>
                      <button
                        type="button"
                        onClick={chip.onClear}
                        className="text-red-600 hover:bg-red-600 hover:text-white rounded-full w-4 h-4 inline-flex items-center justify-center leading-none"
                        aria-label={`Remover ${chip.label}`}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8" data-cy="suppliers-loading">Carregando...</div>
          ) : filteredSuppliers && filteredSuppliers.length > 0 ? (
            <>
              <div className="space-y-3 md:hidden">
                {paginatedSuppliers.map((supplier) => (
                  <div
                    key={supplier.id}
                    className="rounded-lg border bg-card shadow-sm transition hover:shadow-md"
                    onClick={() => handleOpenDialog(supplier)}
                    data-cy={`supplier-card-${supplier.id}`}
                  >
                    <div className="flex flex-col gap-3 px-4 py-3">
                      <div className="flex items-center gap-3">
                        <SupplierAvatar
                          fotoUrl={supplier.foto_url}
                          chatwootAvatarUrl={supplier.chatwoot_avatar_url}
                          email={supplier.email}
                          nome={supplier.nome}
                          size={48}
                        />
                        <div className="flex flex-col gap-1 flex-1">
                          <div className="text-sm text-muted-foreground">Fornecedor</div>
                          <h3 className="text-base font-semibold leading-tight">
                            {supplier.nome}
                          </h3>
                          {supplier.cnpj_cpf && (
                            <span className="text-xs text-muted-foreground">{formatCpfCnpj(supplier.cnpj_cpf)}</span>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-2 text-sm text-muted-foreground">
                        <div>
                          <span className="font-medium text-foreground">Contato / WhatsApp:</span>
                          <div className="flex flex-col mt-1">
                            <span>{supplier.contato || "-"}</span>
                            {supplier.whatsapp && (
                              <span className="text-xs">{getWhatsappDisplay(supplier)}</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <span className="font-medium text-foreground">E-mail / Telefone:</span>
                          <div className="flex flex-col mt-1">
                            {supplier.email && (
                              <span>{supplier.email}</span>
                            )}
                            {supplier.telefone && (
                              <span>{formatPhoneBR(supplier.telefone)}</span>
                            )}
                            {!supplier.email && !supplier.telefone && (
                              <span>-</span>
                            )}
                          </div>
                        </div>
                        {supplier.endereco_completo && (
                          <div>
                            <span className="font-medium text-foreground">Endereço:</span> {supplier.endereco_completo}
                          </div>
                        )}
                      </div>
                      {hasToken && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-muted-foreground">Chatwoot:</span>
                          {renderChatwootStatus(supplier)}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 border-t px-4 py-3 sm:flex-row">
                      <div className="flex w-full gap-2">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSupplierProfile({ id: supplier.id, name: supplier.nome });
                                  }}
                                  data-cy={`supplier-view-btn-mobile-${supplier.id}`}
                                >
                                  <FileText className="mr-2 h-4 w-4" />
                                  Detalhes
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-center">
                                  <p>Ver ficha completa e relatórios do fornecedor</p>
                                  <p className="font-bold">{supplier.nome}</p>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDialog(supplier);
                                  }}
                                  data-cy={`supplier-edit-btn-mobile-${supplier.id}`}
                                >
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Editar
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-center">
                                  <p>Editar fornecedor</p>
                                  <p className="font-bold">{supplier.nome}</p>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1 text-destructive"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteId(supplier.id);
                                  }}
                                  data-cy={`supplier-delete-btn-mobile-${supplier.id}`}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Excluir
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-center">
                                  <p>Excluir fornecedor</p>
                                  <p className="font-bold">{supplier.nome}</p>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden md:block">
                <Table data-cy="suppliers-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome / CPF / CNPJ</TableHead>
                      <TableHead>Contato / WhatsApp</TableHead>
                      <TableHead>E-mail / Telefone</TableHead>
                      {hasToken && <TableHead>Chatwoot</TableHead>}
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-cy="suppliers-table-body">
                    {paginatedSuppliers.map((supplier) => (
                      <TableRow 
                        key={supplier.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleOpenDialog(supplier)}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-3">
                            <SupplierAvatar
                              fotoUrl={supplier.foto_url}
                              chatwootAvatarUrl={supplier.chatwoot_avatar_url}
                              email={supplier.email}
                              nome={supplier.nome}
                              size={40}
                            />
                            <div className="flex flex-col">
                              <span>{supplier.nome}</span>
                              {supplier.cnpj_cpf && (
                                <span className="text-xs text-muted-foreground">{formatCpfCnpj(supplier.cnpj_cpf)}</span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span>{supplier.contato || "-"}</span>
                            {supplier.whatsapp && (
                              <span className="text-xs text-muted-foreground">{getWhatsappDisplay(supplier)}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            {supplier.email && (
                              <span>{supplier.email}</span>
                            )}
                            {supplier.telefone && (
                              <span>{formatPhoneBR(supplier.telefone)}</span>
                            )}
                            {!supplier.email && !supplier.telefone && (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </div>
                        </TableCell>
                        {hasToken && <TableCell>{renderChatwootStatus(supplier)}</TableCell>}
                        <TableCell className="text-right space-x-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="icon-chip-blue"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSupplierProfile({ id: supplier.id, name: supplier.nome });
                                  }}
                                  data-cy={`supplier-view-btn-${supplier.id}`}
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-center">
                                  <p>Ver ficha completa e relatórios do fornecedor</p>
                                  <p className="font-bold">{supplier.nome}</p>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="icon-chip-green"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDialog(supplier);
                                  }}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-center">
                                  <p>Editar fornecedor</p>
                                  <p className="font-bold">{supplier.nome}</p>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="icon-chip-red"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteId(supplier.id);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-center">
                                  <p>Excluir fornecedor</p>
                                  <p className="font-bold">{supplier.nome}</p>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <TablePagination
                currentPage={currentPage}
                totalPages={Math.ceil(filteredSuppliers.length / itemsPerPage)}
                totalItems={filteredSuppliers.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(value) => {
                  setItemsPerPage(value);
                  setCurrentPage(1);
                }}
              />
            </>
          ) : suppliers && suppliers.length > 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-cy="suppliers-empty-filtered">
              Nenhum fornecedor encontrado com os filtros aplicados.
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground" data-cy="suppliers-empty">
              Nenhum fornecedor cadastrado. Clique em "Novo Fornecedor" para começar.
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-cy="supplier-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
  Tem certeza que deseja excluir este fornecedor? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-cy="supplier-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              data-cy="supplier-delete-confirm"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {supplierProfile && (() => {
        const s = (suppliers || []).find((x: any) => x.id === supplierProfile.id);
        const photo = (s as any)?.foto_url || null;
        const infoItems = [
          (s as any)?.created_at ? { value: `Fornecedor desde ${new Intl.DateTimeFormat('pt-BR').format(new Date((s as any).created_at))}` } : null,
          (s as any)?.telefone ? { value: formatPhone((s as any).telefone as string) } : null,
          (s as any)?.email ? { value: (s as any).email } : null,
        ].filter(Boolean) as any;
        return (
          <RaioXModal
            open={!!supplierProfile}
            onOpenChange={() => setSupplierProfile(null)}
            imageUrl={photo || null}
            entityTypeLabel={"FORNECEDOR"}
            entityName={supplierProfile.name}
            infoItems={infoItems}
            renderContent={({ periodRange, setLoading }) => (
              <SupplierDetails id={supplierProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
            )}
          />
        );
      })()}
    </div>
  );
}
